import { useParams } from "react-router-dom";
import {
  Skeleton,
  Button,
  Link,
  Typography,
  List,
  ListItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import DocumentViewer from "components/editor/DocumentViewer";
import { useFetchPublicAdvisorProfile } from "services/public";
import { UserAvatar, DefaultAvatar } from "components/common/CurrentUserAvatar";
import AdvisorDetails from "components/advisor/AdvisorDetails";
import useIsMobile from "components/hooks/useIsMobile";
import { ReactComponent as FayeIcon } from "../icons/FayeLight.svg";
import AdvisorSpecialties from "components/advisor/AdvisorSpecialties";
import FlexPage from "components/layout/FlexPage";
import FlexBox from "components/common/FlexBox";
import { FAQ } from "components/signup/components/FAQ";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  advisorProfileTrackingCode,
  advisorProfileTrackingCodeToLanding,
} from "components/referrals/utils";
import { PublicAdvisor } from "protogen/common_pb";
import useIsDesktop from "components/hooks/useIsDesktop";

const Sidebar = ({
  advisor,
}: {
  advisor: PublicAdvisor | undefined | null;
}) => {
  const navigate = useNavigate();
  const listItems = [
    "Register the kids for activities",
    "Meal plan and order grocery delivery",
    "Plan your next family vacation",
    "Source home contractors and schedule visits",
    "Send flowers for your bestie's birthday",
  ];

  const handleSignupClick = () => {
    if (advisor?.ref) {
      const trackingCode = advisorProfileTrackingCode(advisor.ref);
      navigate(`/signup?ap=${trackingCode}`);
    } else {
      navigate("/signup");
    }
  };

  return (
    <FlexBox direction="column">
      <FlexBox direction="column">
        <Typography variant="h4">Getting started is easy</Typography>
        <Typography variant="body" sx={{ marginTop: "-4px" }}>
          Answer a few quick questions to set up your account and start handing
          off tasks to your Advisor.
        </Typography>

        <FAQ
          question="What can a Faye Advisor do?"
          answer={
            <FlexBox direction="column">
              <Typography variant="body" sx={{ marginTop: "-4px" }}>
                Think of me as your behind-the-scenes partner for the things
                that steal your time and headspace.
              </Typography>
              <Typography variant="body">
                Some of the tasks I regularly take on:
              </Typography>
              <List sx={{ listStyleType: "disc" }}>
                {listItems.map((item) => (
                  <ListItem
                    sx={{ display: "list-item", marginLeft: 2, padding: "0px" }}
                    key={item}
                  >
                    {item}
                  </ListItem>
                ))}
              </List>

              <Typography variant="body">
                With Faye, I can also tap into a national network of Advisors,
                unlock exclusive travel perks, and connect you with
                community-vetted specialists.
              </Typography>
            </FlexBox>
          }
        />

        <Button variant="contained" onClick={handleSignupClick}>
          Get started
        </Button>
      </FlexBox>
    </FlexBox>
  );
};

export default () => {
  let params = useParams();
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();

  const theme = useTheme();
  useState<boolean>(false);
  const { request, data, loading, error } = useFetchPublicAdvisorProfile();

  useEffect(() => {
    const profilePath = params.profilePath!;
    request({ profilePath });
  }, [params.profilePath]);

  const advisor = data?.advisor;
  return (
    <FlexPage
      sx={{
        padding: isMobile ? "0" : "0 24px 24px",
        maxWidth: "1200px",
        gap: isMobile ? "0" : "24px",
      }}
    >
      {/* Header */}
      <FlexBox
        justifyContent="space-between"
        sx={{
          borderBottom: "1px solid #E8E8E8",
          padding: isMobile ? "16px 24px" : "16px 0",
        }}
      >
        <Link
          href={`https://findfaye.com${
            advisor?.ref
              ? `?ap=${advisorProfileTrackingCodeToLanding(advisor.ref)}`
              : ""
          }`}
          target="_blank"
          underline="none"
          sx={{
            "& a": { height: "40px" },
            "& svg path": {
              fill: "#198282",
            },
          }}
        >
          <FayeIcon />
        </Link>

        <Button variant={"outlined"} aria-label="Learn more">
          <Link
            href={`https://findfaye.com${
              advisor?.ref
                ? `?ap=${advisorProfileTrackingCodeToLanding(advisor.ref)}`
                : ""
            }`}
            target="_blank"
            underline="none"
          >
            Learn more
          </Link>
        </Button>
      </FlexBox>

      {/* Main content */}
      <FlexBox
        stackedMobile
        alignItems="start"
        gap={isMobile ? "16px" : "24px"}
        sx={{
          flexDirection: isDesktop ? "row" : "column",
          padding: isMobile ? "8px" : "",
        }}
      >
        {/* Left */}
        <FlexBox
          variant="contained"
          direction="column"
          sx={{
            flex: isMobile ? 1 : 5,
            padding: isMobile ? "24px" : "32px",
            border: "none",
            boxShadow: theme.elevation[1],
          }}
        >
          {error && <div>Advisor Not Found</div>}
          {!error && (
            <FlexBox direction="column" gap="32px">
              <FlexBox gap="24px">
                {advisor ? (
                  <UserAvatar
                    clickable={false}
                    user={advisor}
                    size={isMobile ? 100 : 158}
                  />
                ) : (
                  <DefaultAvatar size={isMobile ? 100 : 158} />
                )}
                <AdvisorDetails
                  advisor={advisor}
                  loading={loading}
                  isPublic={true}
                />
              </FlexBox>

              <FlexBox direction="column">
                <Typography variant="h3">About me</Typography>
                {loading ? (
                  <Skeleton width="80%" />
                ) : (
                  <DocumentViewer content={advisor?.blurbContent || ""} />
                )}
              </FlexBox>

              <AdvisorSpecialties advisor={advisor} />
            </FlexBox>
          )}
        </FlexBox>

        {/* Right */}
        <FlexBox
          variant="contained"
          sx={{
            flex: isMobile ? 1 : 2,
            border: "none",
            boxShadow: theme.elevation[1],
            padding: isMobile ? "24px" : "24px",
          }}
        >
          <Sidebar advisor={advisor} />
        </FlexBox>
      </FlexBox>
    </FlexPage>
  );
};
